// ===== BEGIN FILE: src/components/report-v2/ReportFAQPage.tsx =====
/**
 * Report FAQ Page — "Ask What the Stars Already Know"
 * Appears just before the ReportClosingPage.
 * One trust statement at the top. 8 clean question cards. CTA at the bottom.
 * No technical sauce revealed.
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';
import type { ReportData } from './types';

interface ReportFAQPageProps {
  data: ReportData;
}

const BRAND = {
  purple:      '#5B21B6',
  purpleLight: '#7C3AED',
  purpleSoft:  '#F5F3FF',
  gold:        '#F59E0B',
  goldLight:   '#FFFBEB',
  text:        '#1F2937',
  textMuted:   '#6B7280',
  border:      '#E5E7EB',
  bgLight:     '#F9FAFB',
};

const FAQS = [
  { icon: '📈', question: 'How will my job go this year — will I get a promotion, or should I be looking for a change?',                      accent: '#1D4ED8' },
  { icon: '💑', question: 'When will I meet my life partner — and how is my married life expected to be?',                                    accent: '#9D174D' },
  { icon: '💼', question: 'My career has felt stuck for years. Should I switch industries, go independent or look abroad?',                   accent: '#0369A1' },
  { icon: '🏠', question: 'When is the most auspicious time to buy property, renovate or shift to a new home?',                               accent: '#065F46' },
  { icon: '👶', question: 'I have been trying to conceive. What does my chart say about children and when is the most favourable period?',    accent: '#7C3AED' },
  { icon: '💰', question: 'Why do I struggle financially despite working hard — and when does my wealth picture finally improve?',            accent: '#B45309' },
  { icon: '⚕️', question: 'My health has been unpredictable lately. Which planets are affecting my vitality and what should I do?',          accent: '#0F766E' },
  { icon: '🌟', question: 'What is the single most important thing I should focus on in the next 12 months?',                                 accent: BRAND.purple },
];

export const ReportFAQPage: React.FC<ReportFAQPageProps> = ({ data }) => {
  const firstName = data.birthDetails.name.split(' ')[0];

  return (
    <Box
      id="section-faq"
      className="report-section"
      sx={{
        background: '#FFFFFF',
        p: 2,
        '@media (min-width: 600px)': { p: '14mm 12mm 10mm' },
        '@media print': { p: '14mm 12mm 10mm' },
      }}
    >
      {/* ── Section heading ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ width: 4, height: 28, borderRadius: 2, background: BRAND.purple, flexShrink: 0, '@media print': { background: BRAND.purple } }} />
        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: BRAND.text, letterSpacing: '-0.01em' }}>
          Ask What the Stars Already Know
        </Typography>
      </Box>

      {/* ── Trust statement banner ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, #1E1B4B 0%, ${BRAND.purple} 60%, #4C1D95 100%)`,
          borderRadius: '12px',
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          '@media print': {
            background: BRAND.purple,
            borderRadius: '6pt',
            p: '12pt',
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <Box component="span" sx={{ fontSize: '1.6rem', flexShrink: 0, mt: '-2px' }}>🤫</Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#fff', lineHeight: 1.4 }}>
            {firstName}, some questions deserve more than a guess. They deserve your chart.
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, mb: 1.5 }}>
          ShreemAstro's AI chat knows your entire birth chart — every planet, every house, every planetary period — 
          and has been trained on thousands of classical Vedic texts. It combines that depth with the 
          discretion of a private journal. You can ask it the questions you would never voice out loud 
          and receive answers that are specific to <em>your</em> chart, not generic horoscope-column advice.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            '@media print': { gridTemplateColumns: 'repeat(3, 1fr)', gap: '6pt' },
          }}
        >
          {[
            { icon: '🔒', label: 'Completely private', sub: 'Only you see your conversations' },
            { icon: '📚', label: 'Classical depth',    sub: 'Rooted in Parashara & Jaimini traditions' },
            { icon: '🎯', label: 'Chart-specific',     sub: 'Every answer tied to your exact placements' },
          ].map((pill, i) => (
            <Box
              key={i}
              sx={{
                background: 'rgba(255,255,255,0.10)',
                borderRadius: '8px',
                p: 1,
                textAlign: 'center',
                '@media print': { borderRadius: '4pt', p: '6pt', background: 'rgba(255,255,255,0.15)' },
              }}
            >
              <Box component="span" sx={{ fontSize: '1rem', display: 'block', mb: 0.25 }}>{pill.icon}</Box>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{pill.label}</Typography>
              <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>{pill.sub}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Question cards (2-column grid) ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 1.1,
          mb: 2.5,
          '@media (min-width: 600px)': { gridTemplateColumns: '1fr 1fr' },
          '@media print': { gridTemplateColumns: '1fr 1fr', gap: '7pt' },
        }}
      >
        {FAQS.map((faq, idx) => (
          <Box
            key={idx}
            sx={{
              border: `1.5px solid`,
              borderColor: BRAND.border,
              borderLeft: `4px solid ${faq.accent}`,
              borderRadius: '8px',
              p: 1.4,
              background: '#FFFFFF',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.1,
              transition: 'box-shadow 0.15s',
              '@media print': {
                borderRadius: '5pt',
                p: '8pt',
                gap: '7pt',
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
              },
            }}
          >
            {/* Number badge */}
            <Box
              sx={{
                flexShrink: 0,
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: faq.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: '1px',
                '@media print': { background: faq.accent },
              }}
            >
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                {idx + 1}
              </Typography>
            </Box>

            {/* Icon + question text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Box component="span" sx={{ fontSize: '0.88rem', flexShrink: 0, mt: '0px' }}>{faq.icon}</Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: BRAND.text, lineHeight: 1.5 }}>
                  {faq.question}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── CTA strip ── */}
      <Box
        sx={{
          background: BRAND.purpleSoft,
          border: `1.5px solid ${BRAND.purpleLight}`,
          borderRadius: '10px',
          p: { xs: 2, sm: 2.5 },
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'space-between',
          '@media print': {
            borderRadius: '5pt',
            p: '10pt',
            background: BRAND.purpleSoft,
            border: `1.5pt solid ${BRAND.purple}`,
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: BRAND.purple, mb: 0.4 }}>
            Your chart already holds the answers.
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', color: BRAND.textMuted, lineHeight: 1.6 }}>
            Start a free 7-day trial at{' '}
            <Box component="a" href="https://shreemastro.com" sx={{ color: BRAND.purple, fontWeight: 700, textDecoration: 'none' }}>
              shreemastro.com
            </Box>
            {' '}— our AI astrologer already knows your full chart and is ready to answer in private.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Box
            component="a"
            href="https://shreemastro.com"
            sx={{
              display: 'inline-block',
              background: BRAND.purple,
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.78rem',
              px: 2.5,
              py: 1,
              borderRadius: '8px',
              textDecoration: 'none',
              letterSpacing: '0.02em',
              '@media print': { background: BRAND.purple, color: '#fff', textDecoration: 'none', borderRadius: '4pt' },
            }}
          >
            Start Free Trial →
          </Box>
          <Typography sx={{ fontSize: '0.65rem', color: BRAND.textMuted }}>
            7 days free · No credit card required
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
